import React, { useState, useRef } from 'react';
import IconFont from '$/components/IconFont';
import { Slider } from 'antd';
import { formatSecondToMinute } from '$/utils/timeUtil';
import styles from './index.module.scss';

interface FullControllerProps {
  visible: boolean;
  playing: boolean;
  currentTime: number;
  duration: number;
  volume: number;
  showDownload: boolean;
  onDownload: () => void;
  onTogglePlay: () => void;
  onSeek: (time: number) => void;
  onVolumeChange: (value: number) => void;
}

export default function FullController({
  visible,
  playing,
  currentTime,
  duration,
  volume,
  showDownload,
  onDownload,
  onTogglePlay,
  onSeek,
  onVolumeChange,
}: FullControllerProps) {
  const [showVolume, setShowVolume] = useState(false);
  const volumeTimerRef = useRef<number | null>(null);

  // 阻止事件冒泡
  const handleControllerClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
  };

  // 进度条拖动
  const handleProgressChange = (value: number) => {
    const time = (value / 100) * duration;
    onSeek(time);
  };

  // 音量图标移入
  const handleVolumeMouseEnter = () => {
    if (volumeTimerRef.current) {
      clearTimeout(volumeTimerRef.current);
    }
    setShowVolume(true);
  };

  // 音量图标移出
  const handleVolumeMouseLeave = () => {
    volumeTimerRef.current = window.setTimeout(() => {
      setShowVolume(false);
    }, 200);
  };

  // 音量图标名称
  const getVolumeIconName = (volumeValue: number) => {
    if (volumeValue === 0) return 'muteBold';
    if (volumeValue > 0.5) return 'volumeFullBold';
    return 'volumeHalfBold';
  };

  // 切换静音
  const handleToggleMute = () => {
    onVolumeChange(volume > 0 ? 0 : 0.5);
  };

  // 下载音频
  const handleDownload = () => {
    onDownload?.();
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const displayProgress = progress === 100 ? 0 : progress;
  const displayTime = progress === 100 ? 0 : currentTime;

  // 父组件控制显示
  if (!visible) {
    return null;
  }

  return (
    <div
      className={styles.fullController}
      onClick={handleControllerClick}
    >
      {/* 进度条 */}
      <Slider
        className={styles.progressBar}
        min={0}
        max={100}
        value={displayProgress}
        tooltip={{ formatter: null }}
        onChange={handleProgressChange}
        onAfterChange={handleProgressChange}
      />

      <div className={styles.controlsRow}>
        {/* 左侧控制 */}
        <div className={styles.leftControls}>
          <IconFont
            name={playing ? 'pauseFill' : 'playFill'}
            fontSize={16}
            color="var(--content-secondary, rgba(17, 18, 20, 0.75))"
            className={styles.playBtn}
            onClick={onTogglePlay}
          />
          <div className={styles.timeDisplay}>
            {formatSecondToMinute(displayTime, 'mm:ss')}
            <span className={styles.duration}>
              /
              {formatSecondToMinute(duration, 'mm:ss')}
            </span>
          </div>
        </div>

        {/* 右侧控制 */}
        <div className={styles.rightControls}>
          {/* 音量控制 */}
          <div
            className={styles.volumeControl}
            onMouseEnter={handleVolumeMouseEnter}
            onMouseLeave={handleVolumeMouseLeave}
          >
            <div className={styles.volumeIcon} onClick={handleToggleMute}>
              <IconFont
                name={getVolumeIconName(volume)}
                fontSize={16}
                color="var(--content-secondary, rgba(17, 18, 20, 0.75))"
              />
            </div>
            {showVolume && (
              <div
                className={styles.volumeSlider}
                onMouseEnter={handleVolumeMouseEnter}
                onMouseLeave={handleVolumeMouseLeave}
              >
                <Slider
                  vertical
                  min={0}
                  max={1}
                  step={0.01}
                  value={volume}
                  onChange={onVolumeChange}
                  tooltip={{ formatter: null }}
                />
              </div>
            )}
          </div>

          {/* 下载按钮 */}
          {showDownload && (
            <div className={styles.downloadBtn} onClick={handleDownload}>
              <IconFont
                name="downloadBold"
                fontSize={16}
                color="var(--content-secondary, rgba(17, 18, 20, 0.75))"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
